import React, { useState, useEffect } from 'react';
import CompanyList from './CompanyList';
import { AggregatedCompany, Market, MarketUnit } from './interfaces';
import CompanyPicker from './CompanyPicker';
import { Report } from 'powerbi-client';
import { ReactComponent as PlusSvg } from '../../../../assets/plus.svg';
import AddMarketModal from './AddMarketModal';
import EditMarketModal from './EditMarketModal';
import { appInsights } from '../../../../App';
import { useAuthStore } from '../../../../features/authentication/stores/authStore';

// Define types

const MarketEditor = ({
  setCompanyNames,
  setExternalCompanies,
  refreshReport,
  reportRef1,
  reportRef2,
  companyNames,
  setExternalSelected,
  selectedCompany,
}: {
  setCompanyNames: (names: string[]) => void;
  setExternalCompanies: (companies: Market[]) => void;
  refreshReport: () => void;
  reportRef1: React.MutableRefObject<Report | null>;
  reportRef2: React.MutableRefObject<Report | null>;
  companyNames: string[];
  setExternalSelected: (name: string) => void;
  selectedCompany: string;
}) => {
  const { username } = useAuthStore();
  const [initCompanies, setInitcompanies] = useState<Market[]>([]);
  const [companies, setCompanies] = useState<AggregatedCompany[]>([]);
  const [markets, setMarkets] = useState<[]>([]);
  const [companyToEdit, setCompanyToEdit] = useState<AggregatedCompany | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleEditClick = (company: AggregatedCompany) => {
    setCompanyToEdit(company);
    setShowEditModal(true);
  };

  const handleSwitchToAdd = () => {
    setShowAddModal(true);

    appInsights.trackEvent({
      name: 'AddCompanyClick',
    });
  };

  useEffect(() => {
    fetchCompanies();
    fetchMarkets();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(
        `https://prod-89.eastus.logic.azure.com:443/workflows/28f8e528c0764ddfa08d4ad456d49287/triggers/manual/paths/invoke/${username}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=xkaRvnnRoqQwSn3TlfP92W_Oz0GEnXQ-Hv7ylf0fNso`
      );
      const data = await response.json();

      setInitcompanies(data);
      setExternalCompanies(data);
      const aggregatedData = aggregateCompanies(data);
      setCompanies(aggregatedData);
      setCompanyNames(aggregatedData.map(company => company.name));
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchMarkets = async () => {
    try {
      const response = await fetch(
        'https://prod-39.eastus.logic.azure.com:443/workflows/9d2d57a1aa124cf9948350e34dd35598/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=5cm7u3QE_oz41v1cABStx9bl4-IisPfDgN81QlABnZE'
      );
      const data = await response.json();

      setMarkets(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const aggregateCompanies = (data: Market[]): AggregatedCompany[] => {
    const companyMap = new Map();
    data.forEach((item: any) => {
      if (companyMap.has(item.company)) {
        const existing = companyMap.get(item.company);
        existing.units += item.units;
        existing.markets += 1; // Assuming each entry represents a different market
      } else {
        companyMap.set(item.company, {
          name: item.company,
          units: item.units,
          markets: 1,
        });
      }
    });
    return Array.from(companyMap.values());
  };

  const handleAddCompany = async (name: string, marketsUnits: MarketUnit[]) => {
    try {
      const newCompany = marketsUnits.map(marketUnit => ({
        company: name,
        market: marketUnit.market,
        units: marketUnit.units,
        user: username,
      }));

      appInsights.trackEvent({
        name: 'AddCompanyPopupSaveClick',
        properties: {
          name: name,
          marketsUnits: marketsUnits,
        },
      });

      const response = await fetch(
        'https://prod-59.eastus.logic.azure.com:443/workflows/e0ee39520b2644eda5d7c788b000d505/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=gxqcoTADwdX4bU9U-gTd3DJsL8lQm4dRW9u8WR6v-ww',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newCompany),
        }
      );
      if (response.ok) {
        fetchCompanies(); // Refresh the list of companies
        setShowAddModal(false);
        refreshReport();
      }
    } catch (error) {
      console.error('Error adding company:', error);
    }
  };

  const handleDeleteCompany = async (name: string) => {
    try {
      const response = await fetch(
        `https://prod-67.eastus.logic.azure.com/workflows/f6d4d41069df4826a7155eb0f9af4b3b/triggers/manual/paths/invoke/${username}/${name}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=AbPRYhDn608Zfx310EnUlhRi4tbGbwsYQbtezV3EiDY`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        fetchCompanies(); // Refresh the list of companies
        refreshReport();
      }
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  };

  const handleEditCompany = async (name: string, marketsUnits: MarketUnit[]) => {
    try {
      const response = await fetch(
        `https://prod-67.eastus.logic.azure.com/workflows/f6d4d41069df4826a7155eb0f9af4b3b/triggers/manual/paths/invoke/${username}/${name}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=AbPRYhDn608Zfx310EnUlhRi4tbGbwsYQbtezV3EiDY`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        try {
          const newCompany = marketsUnits.map(marketUnit => ({
            company: name,
            market: marketUnit.market,
            units: marketUnit.units,
            user: username, // Assuming the user is static for this example
          }));

          const response = await fetch(
            'https://prod-59.eastus.logic.azure.com:443/workflows/e0ee39520b2644eda5d7c788b000d505/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=gxqcoTADwdX4bU9U-gTd3DJsL8lQm4dRW9u8WR6v-ww',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(newCompany),
            }
          );
          if (response.ok) {
            fetchCompanies(); // Refresh the list of companies
            setShowEditModal(false);
            refreshReport();
          }
        } catch (error) {
          console.error('Error adding company:', error);
        }
      }
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  };

  // Getter function to find MarketUnits based on companyToEdit's name
  const getMarketUnits = (): MarketUnit[] => {
    if (companyToEdit) {
      return initCompanies
        .filter(c => c.company === companyToEdit.name)
        .map(c => ({
          market: c.market,
          units: c.units,
        }));
    }
    return [];
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        alignItems: 'baseline',
      }}
    >
      {showAddModal && (
        <AddMarketModal
          onClose={() => {
            setShowAddModal(false);
          }}
          markets={markets}
          existingNames={companies.map(c => c.name)}
          onAddCompany={handleAddCompany}
        />
      )}
      {showEditModal && companyToEdit && (
        <EditMarketModal
          onClose={() => {
            setShowEditModal(false);
          }}
          onEditCompany={handleEditCompany}
          initialCompanyName={companyToEdit.name}
          initialMarketsUnits={getMarketUnits()}
          markets={markets}
        />
      )}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          width: '100%',
          alignItems: 'center',
          gap: '0.8vh',
        }}
      >
        <CompanyPicker
          reportRef1={reportRef1}
          reportRef2={reportRef2}
          companyNames={companyNames}
          setExternalSelected={setExternalSelected}
          selectedCompany={selectedCompany}
        />
        <button style={styles.button} onClick={handleSwitchToAdd}>
          <PlusSvg fill="white" height={'2.1vh'} width={'2.1vh'} />
        </button>
      </div>
      <div style={{ height: '0.8vh' }} />
      <div style={{ ...styles.container, height: '23.7vh' }}>
        <CompanyList companies={companies} onEditClick={handleEditClick} onDeleteClick={handleDeleteCompany} />
      </div>
    </div>
  );
};

// Inline styles for simplicity
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
    overflowY: 'scroll',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
    backgroundColor: '#fff',
    borderRadius: '1.6vh',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '4vh',
    height: '4vh',
    borderRadius: '50%',
    backgroundColor: '#00AB9B', // Adjust the color to match the image
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  hover: {
    backgroundColor: '#00897B',
  },
};

export default MarketEditor;
